import { TextAlignment, TextVerticalAlignment } from "building-types/annotation/annotationTypesEnum";
import { ControllerSwitchType } from "building-types/electrical/electricalTypesEnum";
import { HUNDRETH_INCH_PER_SVG_UNIT, SVG_UNITS_PER_100TH_INCH } from "common/constants/pdfSettings";
import { FontId } from "common/constants/textFont";
import { radianToDegree } from "common/utils/math";
import { numMul } from "common/utils/nominalNumbers";
import { mat3CSSRotate as rotate, mat3Dot, mat3Reduce, mat3Scale, mat3Translate as translate } from "geom/mat3";
import type { Vec2 } from "geom/vec2";
import { vec2AngleBetween, vec2AngleBetweenCW, vec2Normalize } from "geom/vec2";
import { jsxToString } from "lib/house/annotation/conversion";
import PlanLabel from "lib/house/annotation/PlanLabel";
import type { BaseAnnoProps, BaseStyleProps } from "lib/house/annotation/svgSettings";
import { baseAnnoDefault, baseStyleDefault } from "lib/house/annotation/svgSettings";

const DIMMER_DEFAULT_PLAN_TAG = "D";
/** Matches the visual center of the legacy three-way / four-way stroked path markers. */
const SWITCH_TYPE_PLAN_TAG_X = 4.9;
const SWITCH_TYPE_PLAN_TAG_Y = 3.35;
/** Extra scale applied to switch symbol geometry beyond pdfScale (matches legacy SVG sizing). */
const CONTROLLER_SYMBOL_SCALE_FACTOR = 1.2;
/** Maps the symbol anchor to the switch body pivot in symbol-local coordinates. */
const CONTROLLER_SYMBOL_ANCHOR_OFFSET_X = -3.3;
const CONTROLLER_SYMBOL_ANCHOR_OFFSET_Y = -15;
/** Pivot for keeping switch-type markers upright in symbol-local coordinates. */
const SWITCH_TYPE_MARKER_UPRIGHT_PIVOT_X = 4;
const SWITCH_TYPE_MARKER_UPRIGHT_PIVOT_Y = 3;
/** Pre-rotation nudge applied around the switch-type marker group. */
const SWITCH_TYPE_MARKER_PRE_ROTATION_NUDGE_Y = -1;

interface ControllerOnlyProps extends BaseAnnoProps {
  normal: Vec2;
  switchType?: ControllerSwitchType;
  planRotationRads?: number;
  svgTextScale?: number;
  /** Custom plan tag; on plans replaces the default switch-type marker text when set. */
  planTag?: string;
  /** Use SVG text for switch-type markers (legend cells). Stroked paths are used in Studio's path-only viewer. */
  dimmerMarkerAsText?: boolean;
  /** Omit switch-type path/text markers (plans and Studio when label is rendered separately). */
  omitSwitchTypeMarker?: boolean;
}

export type ControllerProps = ControllerOnlyProps &
  Pick<
    BaseStyleProps,
    | "fillLightPrimary"
    | "strokeDarkPrimary"
    | "smallStrokeWidth"
    | "textFillPrimary"
    | "textStrokePrimary"
    | "textStrokeWidth"
    | "regSmallFontSize"
    | "smallFontSize"
    | "fontFamily0"
  >;

type ResolvedControllerProps = Required<Omit<ControllerProps, "planTag">> & Pick<ControllerProps, "planTag">;

export const controllerDefault: Required<Omit<ControllerProps, "planTag">> = {
  ...baseStyleDefault,
  ...baseAnnoDefault,
  normal: [0, 1],
  switchType: ControllerSwitchType.FOUR_WAY,
  planRotationRads: 0,
  svgTextScale: 1,
  dimmerMarkerAsText: false,
  omitSwitchTypeMarker: false,
};

export function normalizeControllerPlanTag(planTag?: string | null): string | undefined {
  if (planTag === "" || planTag === null || planTag === undefined) {
    return undefined;
  }
  return planTag;
}

export function generateController(props?: Partial<ControllerProps>, wrapSvg = false): string {
  return jsxToString(<Controller {...resolveControllerProps(props)} />, wrapSvg);
}

function resolveControllerProps(props?: Partial<ControllerProps>): ResolvedControllerProps {
  const resolved = { ...controllerDefault, ...props };
  return {
    ...resolved,
    planTag: normalizeControllerPlanTag(resolved.planTag),
  };
}

/** World-space offset from controller anchor to the switch-type plan tag center. */
export function controllerSwitchTypePlanTagWorldOffset(props: {
  normal: Vec2;
  scale?: number;
  planRotationRads?: number;
}): Vec2 {
  const pdfScale = (props.scale ?? 1) * SVG_UNITS_PER_100TH_INCH;
  const symbolScale = pdfScale * CONTROLLER_SYMBOL_SCALE_FACTOR;
  const inverseScale = 1 / symbolScale;
  const [oX, oY] = vec2Normalize(props.normal[0], props.normal[1]);
  const angleCW = radianToDegree(vec2AngleBetweenCW(0, 1, oX, oY));
  const uprightAngle = radianToDegree(vec2AngleBetween(0, 1, oX, oY) - (props.planRotationRads ?? 0));

  const symbolTransform = mat3Reduce([
    mat3Scale(symbolScale, symbolScale),
    rotate(angleCW),
    translate(CONTROLLER_SYMBOL_ANCHOR_OFFSET_X, CONTROLLER_SYMBOL_ANCHOR_OFFSET_Y),
    translate(0, SWITCH_TYPE_MARKER_PRE_ROTATION_NUDGE_Y),
    rotate(uprightAngle, SWITCH_TYPE_MARKER_UPRIGHT_PIVOT_X, SWITCH_TYPE_MARKER_UPRIGHT_PIVOT_Y),
    translate(0, SWITCH_TYPE_MARKER_PRE_ROTATION_NUDGE_Y),
    translate(SWITCH_TYPE_PLAN_TAG_X, SWITCH_TYPE_PLAN_TAG_Y),
    mat3Scale(inverseScale, inverseScale),
  ]);

  return mat3Dot(symbolTransform, 0, 0);
}

/** Editor offset/rotation for switch-type tags, matching SVGLoader path scaling in the Studio viewer. */
export function controllerSwitchTypePlanTagEditorPlacement(props: {
  normal: Vec2;
  scale?: number;
  planRotationRads?: number;
}): { offset: Vec2; uprightAngleRads: number } {
  const [offsetX, offsetY] = controllerSwitchTypePlanTagWorldOffset(props);
  const editorSvgScale = HUNDRETH_INCH_PER_SVG_UNIT / 2;
  // Unlike the SVG/PDF path, the editor tag isn't nested inside a wall-angle-rotated parent
  // group, so the wall-normal-relative correction term must not be included here
  const uprightAngleRads = 0;

  return {
    offset: [offsetX * editorSvgScale, -offsetY * editorSvgScale],
    uprightAngleRads,
  };
}

export function getControllerSwitchTypePlanTag(props: {
  switchType?: ControllerSwitchType;
  planTag?: string;
}): string | undefined {
  return getSwitchTypePlanTag({
    dimmerMarkerAsText: true,
    omitSwitchTypeMarker: false,
    switchType: props.switchType,
    planTag: props.planTag,
  });
}

const Controller = (props: ResolvedControllerProps) => {
  const scale = (props.scale ?? 1) * SVG_UNITS_PER_100TH_INCH;
  const [rawX, rawY] = props.normal;
  const [oX, oY] = vec2Normalize(rawX, rawY);
  const angleCW = radianToDegree(vec2AngleBetweenCW(0, 1, oX, oY));
  const uprightAngle = keepTextUprightAngle(props);
  const inverseScale = 1 / (scale * CONTROLLER_SYMBOL_SCALE_FACTOR);

  const switchTypePlanTag = props.omitSwitchTypeMarker ? undefined : getSwitchTypePlanTag(props);

  let switchTypeElement = <></>;
  if (switchTypePlanTag) {
    switchTypeElement = renderSwitchTypePlanTag(props, switchTypePlanTag, inverseScale);
  } else if (!props.omitSwitchTypeMarker && props.planTag === undefined) {
    switch (props.switchType) {
      case ControllerSwitchType.THREE_WAY: {
        switchTypeElement = (
          <>
            <path
              fill="none"
              d="M3.62311 4.45018C3.62311 5.11044 4.12168 5.5147 4.84933 5.52817C5.57697 5.54165 6.08902 5.0296 6.10249 4.45018C6.11597 3.87076 6.03512 3.15659 4.29685 3.21049"
              strokeWidth={props.smallStrokeWidth}
              stroke={props.strokeDarkPrimary}
            />
            <path
              fill="none"
              d="M3.71993 2.06204C3.71993 1.45333 4.17958 1.08063 4.85042 1.06821C5.52126 1.05578 5.99334 1.52786 6.00576 2.06204C6.01818 2.59623 5.94364 3.25465 4.34108 3.20496"
              strokeWidth={props.smallStrokeWidth}
              stroke={props.strokeDarkPrimary}
            />
          </>
        );
        break;
      }
      case ControllerSwitchType.FOUR_WAY: {
        switchTypeElement = (
          <>
            <path
              fill="none"
              d="M5.59045 0.973633V5.85155"
              strokeWidth={props.smallStrokeWidth}
              stroke={props.strokeDarkPrimary}
            />
            <path
              fill="none"
              d="M6.5 4.49059H3.29297"
              strokeWidth={props.smallStrokeWidth}
              stroke={props.strokeDarkPrimary}
            />
            <path
              fill="none"
              d="M5.66552 1.14467H5.42572L3.4407 4.40973"
              strokeWidth={props.smallStrokeWidth}
              stroke={props.strokeDarkPrimary}
            />
          </>
        );
        break;
      }
      case ControllerSwitchType.STANDARD: {
        break;
      }
      case ControllerSwitchType.DIMMER: {
        // Stroked paths for Studio's SVG path loader (editor electrical layer).
        switchTypeElement = (
          <>
            <path
              fill="none"
              d="M3.85 2.1 V4.9"
              strokeWidth={props.smallStrokeWidth}
              stroke={props.strokeDarkPrimary}
            />
            <path
              fill="none"
              d="M3.85 2.1 Q5.95 3.5 3.85 4.9"
              strokeWidth={props.smallStrokeWidth}
              stroke={props.strokeDarkPrimary}
            />
          </>
        );
        break;
      }
      case undefined:
    }
  }

  return (
    <g
      transform={`translate(${props.x} ,${props.y}) scale(${scale * CONTROLLER_SYMBOL_SCALE_FACTOR},${
        scale * CONTROLLER_SYMBOL_SCALE_FACTOR
      }) rotate(${angleCW} 0,0) translate(${CONTROLLER_SYMBOL_ANCHOR_OFFSET_X},${CONTROLLER_SYMBOL_ANCHOR_OFFSET_Y})`}
    >
      <path
        fill="none"
        d="M5.48438 7.17187C5.17969 6.99219 4.3625 6.63125 3.53125 6.625C2.49219 6.61719 1.59375 7.22656 1.57031 8.00781C1.57031 8.89062 2.05469 9.39062 3.32031 9.85156C4.58594 10.3125 5.58594 11.0234 5.125 12.3125C4.66406 13.6016 2.5625 13.6406 0.96875 12.6797"
        strokeWidth={props.smallStrokeWidth}
        stroke={props.strokeDarkPrimary}
      />
      <path fill="none" d="M3.53906 5V6.62492" strokeWidth={props.smallStrokeWidth} stroke={props.strokeDarkPrimary} />
      <path fill="none" d="M3.28906 15V13.3438" strokeWidth={props.smallStrokeWidth} stroke={props.strokeDarkPrimary} />
      {switchTypeElement && (
        <g
          transform={`translate(0,${SWITCH_TYPE_MARKER_PRE_ROTATION_NUDGE_Y}) rotate(${uprightAngle} ${SWITCH_TYPE_MARKER_UPRIGHT_PIVOT_X},${SWITCH_TYPE_MARKER_UPRIGHT_PIVOT_Y}) translate(0,${SWITCH_TYPE_MARKER_PRE_ROTATION_NUDGE_Y})`}
        >
          {switchTypeElement}
        </g>
      )}
    </g>
  );
};

export default Controller;

function getSwitchTypePlanTag(props: {
  dimmerMarkerAsText?: boolean;
  omitSwitchTypeMarker?: boolean;
  planTag?: string;
  switchType?: ControllerSwitchType;
}): string | undefined {
  if (!props.dimmerMarkerAsText) {
    return undefined;
  }
  const planTag = normalizeControllerPlanTag(props.planTag);
  if (planTag !== undefined) {
    return planTag;
  }
  switch (props.switchType) {
    case ControllerSwitchType.THREE_WAY: {
      return "3";
    }
    case ControllerSwitchType.FOUR_WAY: {
      return "4";
    }
    case ControllerSwitchType.DIMMER: {
      return DIMMER_DEFAULT_PLAN_TAG;
    }
    default: {
      return undefined;
    }
  }
}

function renderSwitchTypePlanTag(props: ResolvedControllerProps, text: string, inverseScale: number) {
  return (
    <PlanLabel
      text={text}
      transform={[translate(SWITCH_TYPE_PLAN_TAG_X, SWITCH_TYPE_PLAN_TAG_Y), mat3Scale(inverseScale, inverseScale)]}
      fill={props.textFillPrimary}
      fontSize={numMul(props.smallFontSize, props.svgTextScale)}
      fontFamily={FontId.ArialNarrow_Bold}
      alignment={TextAlignment.CENTER}
      verticalAlignment={TextVerticalAlignment.CENTER}
      stroke={props.textStrokePrimary}
      strokeWidth={props.textStrokeWidth}
    />
  );
}

function keepTextUprightAngle(props: ResolvedControllerProps) {
  const [rawX, rawY] = props.normal;
  const [oX, oY] = vec2Normalize(rawX, rawY);
  const angleCW = radianToDegree(vec2AngleBetween(0, 1, oX, oY) - (props.planRotationRads ?? 0));
  return angleCW;
}
